/**
 * GIS Services system-data loader.
 *
 * Reads the shared `gisServicesLayersStore` cache (WFS + WMS services lists,
 * loaded eagerly by the root layout) and projects it into the nested product →
 * version → module catalog the UI consumes. The full operational layer set
 * (`operationalLayersStore`) is passed alongside so each catalog entry can match
 * its root layer for the preview map's tiles + bounds.
 *
 * Only products listed in `buildCatalog`'s `PRODUCT_ORDER` are surfaced;
 * anything else is filtered out to avoid empty product cards.
 */

import { getLogger } from '@logtape/logtape';
import { gisServicesLayersStore } from '#lib/stores/gisServicesLayersStore.svelte.js';
import { operationalLayersStore } from '#lib/stores/operationalLayersStore.svelte.js';
import { FetchStatus } from '#lib/types.js';
import type { IGisServicesSystemData } from './interfaces';
import { buildCatalog } from './buildCatalog';

const log = getLogger(['platform', 'gis-services', 'loaders']);
// Assembles the catalog; null when the key has no layers of a supported product, throws on fetch failure.
export async function loadSystem(apiKey: string): Promise<IGisServicesSystemData | null> {
	if (gisServicesLayersStore.status !== FetchStatus.SUCCESS) {
		await gisServicesLayersStore.load(apiKey);
	}
	if (operationalLayersStore.status !== FetchStatus.SUCCESS) {
		await operationalLayersStore.load(apiKey);
	}
	if (gisServicesLayersStore.status === FetchStatus.FAILURE) {
		throw new Error(gisServicesLayersStore.error ?? 'Failed to load GIS Services layers');
	}

	const catalog = buildCatalog(
		gisServicesLayersStore.wfsLayers,
		gisServicesLayersStore.wmsLayers,
		operationalLayersStore.layers
	);

	log.info('Catalog assembled: {productCount} product(s), {layerCount} layer(s)', {
		productCount: catalog.productOrder.length,
		layerCount: Object.keys(catalog.layerById).length
	});

	return catalog.productOrder.length > 0 ? catalog : null;
}
